import React, { useState, useEffect } from 'react';
import { getOCELMetadata, apiClient } from '../../../services/apiClient';

/**
 * EventAttributeFilterControl - Control for event attribute filters
 *
 * Allows adding multiple attribute-value filter pairs with enable/disable toggles.
 * Fetches available event attributes from metadata and their possible values.
 *
 * @param {Array} value - Current event attribute filters [{ attribute, value, enabled }]
 * @param {Function} onChange - Callback when filters change
 * @param {string} activeFile - Current active file for fetching metadata
 * @param {boolean} disabled - Whether control is disabled
 */
const EventAttributeFilterControl = ({ value = [], onChange, activeFile, disabled = false }) => {
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [attributeValues, setAttributeValues] = useState({}); // Cache of values per attribute
  const [loading, setLoading] = useState(false);
  const [loadingValues, setLoadingValues] = useState({});

  // Fetch available event attribute names from metadata
  useEffect(() => {
    const fetchAttributes = async () => {
      if (!activeFile) return;

      setLoading(true);
      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log('📊 EventAttributeFilterControl - Fetched metadata:', metadata);

        if (metadata?.event_attribute_names) {
          const attributes = Array.isArray(metadata.event_attribute_names)
            ? metadata.event_attribute_names
            : Object.keys(metadata.event_attribute_names);

          setAvailableAttributes(attributes);
          console.log(`✅ EventAttributeFilterControl - Loaded ${attributes.length} event attributes`);
        }
      } catch (err) {
        console.error('❌ EventAttributeFilterControl - Failed to fetch attributes:', err);
        setAvailableAttributes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [activeFile]);

  // Fetch possible values for a specific attribute
  const fetchAttributeValues = async (attribute) => {
    if (!activeFile || !attribute) return;
    if (attributeValues[attribute]) return; // Already cached

    setLoadingValues(prev => ({ ...prev, [attribute]: true }));
    try {
      const response = await apiClient.get(`/ocel/event-attributes/${encodeURIComponent(attribute)}/values`, {
        params: { file_type: activeFile }
      });

      const values = response.data;
      setAttributeValues(prev => ({ ...prev, [attribute]: values }));
      console.log(`✅ EventAttributeFilterControl - Loaded ${values.length} values for attribute "${attribute}"`);
    } catch (err) {
      console.error(`❌ EventAttributeFilterControl - Failed to fetch values for attribute "${attribute}":`, err);
      setAttributeValues(prev => ({ ...prev, [attribute]: [] }));
    } finally {
      setLoadingValues(prev => ({ ...prev, [attribute]: false }));
    }
  };

  const handleAddFilter = () => {
    if (disabled) return;

    const newFilter = { attribute: '', value: '', enabled: true };
    onChange([...value, newFilter]);
  };

  const handleRemoveFilter = (index) => {
    if (disabled) return;

    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handleFilterChange = (index, field, newValue) => {
    if (disabled) return;

    const newFilters = [...value];
    newFilters[index] = { ...newFilters[index], [field]: newValue };

    // If attribute changed, fetch its values and reset value field
    if (field === 'attribute' && newValue) {
      fetchAttributeValues(newValue);
      newFilters[index].value = '';
    }

    onChange(newFilters);
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Event Attribute Filters</label>
        <div className="text-sm text-gray-500 italic">Loading event attributes...</div>
      </div>
    );
  }

  if (availableAttributes.length === 0 && !loading) {
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">Event Attribute Filters</label>
        <div className="text-sm text-gray-500 italic">No event attributes available</div>
      </div>
    );
  }

  const activeCount = value.filter(f => f.enabled && f.attribute && f.value).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Event Attribute Filters
          {activeCount > 0 && (
            <span className="ml-2 text-xs text-gray-500">({activeCount} active)</span>
          )}
        </label>
      </div>

      <div className="space-y-2">
        {value.map((filter, index) => {
          const values = attributeValues[filter.attribute] || [];
          const isLoadingValues = loadingValues[filter.attribute];

          return (
            <div key={index} className="flex items-start gap-2 p-3 bg-gray-50 rounded-md border border-gray-200">
              <div className="flex-1 space-y-2">
                {/* Attribute Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Attribute
                  </label>
                  <select
                    value={filter.attribute}
                    onChange={(e) => handleFilterChange(index, 'attribute', e.target.value)}
                    disabled={disabled}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select attribute...</option>
                    {availableAttributes.map((attr) => (
                      <option key={attr} value={attr}>
                        {attr}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Value Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Value
                  </label>
                  {isLoadingValues ? (
                    <div className="text-xs text-gray-500 italic py-1.5">Loading values...</div>
                  ) : values.length > 0 ? (
                    <select
                      value={filter.value}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      disabled={disabled || !filter.attribute}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select value...</option>
                      {values.map((val) => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={filter.value}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      disabled={disabled || !filter.attribute}
                      placeholder="Enter value..."
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col items-center gap-2 pt-6">
                <label className="flex items-center cursor-pointer" title={filter.enabled ? 'Enabled' : 'Disabled'}>
                  <input
                    type="checkbox"
                    checked={filter.enabled}
                    onChange={(e) => handleFilterChange(index, 'enabled', e.target.checked)}
                    disabled={disabled}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => handleRemoveFilter(index)}
                  disabled={disabled}
                  className="text-red-600 hover:text-red-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                  title="Remove filter"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Filter Button */}
      <button
        type="button"
        onClick={handleAddFilter}
        disabled={disabled}
        className="w-full px-3 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        + Add Filter
      </button>

      {value.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No event attribute filters configured
        </div>
      )}
    </div>
  );
};

export default EventAttributeFilterControl;
